import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { X } from "lucide-react";

import { useUserGroupPops } from "../hooks/useGroupMemberPops";
import { useUserPopsViews } from "../../../saved/users/hooks/useUserPopsViews";
import { useUserSavedPops } from "../../../saved/users/hooks/useUserSavedPops";
import PopThumbnailCard from "../../../../../components/cards/pops/PopThumbnailCard";

import "./GroupMemberPopsFeed.css";

const POPS_LIMIT = 12;
const MIN_WATCH_SECONDS = 3;
const MAX_VIEW_IDS_PER_REQUEST = 50;

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizePop = (response) => {
  if (!response || response.success === false) return null;

  const pop = response.pop || response.data?.pop || response.data || response;

  if (!pop || typeof pop !== "object" || !pop.popTag) {
    return null;
  }

  return {
    ...pop,
    videoUrl: pop.videoUrl || pop.playbackUrl || pop.hlsUrl || "",
  };
};

const normalizeViewResult = (result) => {
  const popId = Number(result?.popId ?? result?.pop_id);

  if (!Number.isSafeInteger(popId) || popId <= 0) {
    return null;
  }

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  return {
    popId,
    realViews,
    demoViews,
    totalViews: normalizeCount(
      result.totalViews ?? result.total_views,
      realViews + demoViews,
    ),
  };
};

export const GroupMemberPopsFeed = ({
  groupTag,
  initialPopTag = null,
  onClose,
  showCloseButton = true,
}) => {
  const {
    popThumbs,
    loading,
    loadingMore,
    error,
    hasMore,
    getUserGroupPopThumbs,
    loadMoreUserGroupPops,
    getUserGroupPop,
    resetUserGroupPops,
  } = useUserGroupPops(groupTag, POPS_LIMIT);

  const { recordPopView, getPopViewCounts, getPopViewCount } =
    useUserPopsViews();

  const {
    isPopSaved,
    isPopSaving,
    hasSavedStatus,
    getPopSavedStatus,
    toggleSavePop,
  } = useUserSavedPops();

  const [activePopTag, setActivePopTag] = useState(
    initialPopTag ? String(initialPopTag) : null,
  );

  const [initialPop, setInitialPop] = useState(null);
  const [playablePops, setPlayablePops] = useState({});
  const [videoErrors, setVideoErrors] = useState({});
  const [viewCounts, setViewCounts] = useState({});
  const [muted, setMuted] = useState(true);

  const containerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const activePopTagRef = useRef(activePopTag);
  const playablePopsRef = useRef({});
  const requestsRef = useRef(new Map());

  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const requestingMoreRef = useRef(false);
  const initialScrollRef = useRef(false);

  const getPopRef = useRef(getUserGroupPop);
  const getThumbsRef = useRef(getUserGroupPopThumbs);
  const loadMoreFnRef = useRef(loadMoreUserGroupPops);
  const resetRef = useRef(resetUserGroupPops);
  const getViewCountsRef = useRef(getPopViewCounts);
  const getSavedStatusRef = useRef(getPopSavedStatus);
  const hasSavedStatusRef = useRef(hasSavedStatus);

  const loadedSavedIdsRef = useRef(new Set());
  const pendingSavedIdsRef = useRef(new Set());
  const loadedViewIdsRef = useRef(new Set());
  const pendingViewIdsRef = useRef(new Set());
  const viewAttemptsRef = useRef(new Set());
  const watchProgressRef = useRef(new Map());

  useEffect(() => {
    getPopRef.current = getUserGroupPop;
    getThumbsRef.current = getUserGroupPopThumbs;
    loadMoreFnRef.current = loadMoreUserGroupPops;
    resetRef.current = resetUserGroupPops;
    getViewCountsRef.current = getPopViewCounts;
    getSavedStatusRef.current = getPopSavedStatus;
    hasSavedStatusRef.current = hasSavedStatus;
  });

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      requestsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    activePopTagRef.current = activePopTag;
  }, [activePopTag]);

  useEffect(() => {
    loadingMoreRef.current = loading || loadingMore;
  }, [loading, loadingMore]);

  const feedPops = useMemo(() => {
    const items = Array.isArray(popThumbs) ? [...popThumbs] : [];

    if (
      initialPop?.popTag &&
      !items.some((item) => String(item?.popTag) === String(initialPop.popTag))
    ) {
      items.unshift(initialPop);
    }

    if (initialPopTag) {
      const index = items.findIndex(
        (item) => String(item?.popTag) === String(initialPopTag),
      );

      if (index > 0) {
        const [selected] = items.splice(index, 1);
        items.unshift(selected);
      }
    }

    const unique = new Map();

    items.forEach((pop) => {
      if (!pop?.popTag) return;

      const key = String(pop.popTag);

      if (!unique.has(key)) {
        unique.set(key, pop);
      }
    });

    return Array.from(unique.values());
  }, [popThumbs, initialPop, initialPopTag]);

  const applyViewCounts = useCallback((results) => {
    if (!Array.isArray(results)) return;

    const nextCounts = {};

    results.forEach((result) => {
      const normalized = normalizeViewResult(result);
      if (!normalized) return;

      const key = String(normalized.popId);
      nextCounts[key] = normalized;
      loadedViewIdsRef.current.add(key);
    });

    if (Object.keys(nextCounts).length) {
      setViewCounts((previous) => ({
        ...previous,
        ...nextCounts,
      }));
    }
  }, []);

  const loadViewCounts = useCallback(
    async (ids, generation = generationRef.current) => {
      const validIds = [
        ...new Set(
          ids.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0),
        ),
      ];

      const missing = validIds.filter((id) => {
        const key = String(id);

        return (
          !loadedViewIdsRef.current.has(key) &&
          !pendingViewIdsRef.current.has(key)
        );
      });

      for (
        let index = 0;
        index < missing.length;
        index += MAX_VIEW_IDS_PER_REQUEST
      ) {
        const batch = missing.slice(index, index + MAX_VIEW_IDS_PER_REQUEST);

        batch.forEach((id) => {
          pendingViewIdsRef.current.add(String(id));
        });

        try {
          const results = await getViewCountsRef.current(batch);

          if (!mountedRef.current || generation !== generationRef.current) {
            return;
          }

          if (!Array.isArray(results)) {
            throw new Error("Invalid Pop view-count response.");
          }

          applyViewCounts(results);
        } catch (viewError) {
          console.error("Unable to retrieve group Pop views:", viewError);
        } finally {
          batch.forEach((id) => {
            pendingViewIdsRef.current.delete(String(id));
          });
        }
      }
    },
    [applyViewCounts],
  );

  const loadPlayablePop = useCallback(
    async (popTag) => {
      const key = String(popTag || "");
      if (!key) return false;

      const cached = playablePopsRef.current[key];
      if (cached?.videoUrl) return cached;

      if (requestsRef.current.has(key)) {
        return requestsRef.current.get(key);
      }

      const generation = generationRef.current;

      const request = (async () => {
        try {
          const response = await getPopRef.current(key);

          if (response?.success === false) {
            throw new Error(response.message || "Unable to load group Pop.");
          }

          const playable = normalizePop(response);

          if (!playable?.videoUrl) {
            throw new Error("Pop playback source is unavailable.");
          }

          if (!mountedRef.current || generation !== generationRef.current) {
            return false;
          }

          const updated = {
            ...playablePopsRef.current,
            [key]: playable,
          };

          playablePopsRef.current = updated;
          setPlayablePops(updated);

          setVideoErrors((previous) => {
            const next = { ...previous };
            delete next[key];
            return next;
          });

          if (playable.popId) {
            void loadViewCounts([playable.popId], generation);
          }

          return playable;
        } catch (loadError) {
          console.error(`Unable to load group Pop ${key}:`, loadError);

          if (mountedRef.current && generation === generationRef.current) {
            setVideoErrors((previous) => ({
              ...previous,
              [key]:
                loadError?.response?.data?.message ||
                loadError?.message ||
                "Unable to load video.",
            }));
          }

          return false;
        } finally {
          requestsRef.current.delete(key);
        }
      })();

      requestsRef.current.set(key, request);
      return request;
    },
    [loadViewCounts],
  );

  useEffect(() => {
    if (!groupTag) return;

    let cancelled = false;
    generationRef.current += 1;

    const generation = generationRef.current;

    requestsRef.current.clear();
    playablePopsRef.current = {};

    setPlayablePops({});
    setVideoErrors({});
    setViewCounts({});
    setInitialPop(null);

    loadedSavedIdsRef.current.clear();
    pendingSavedIdsRef.current.clear();
    loadedViewIdsRef.current.clear();
    pendingViewIdsRef.current.clear();
    viewAttemptsRef.current.clear();
    watchProgressRef.current.clear();

    initialScrollRef.current = false;

    const requestedTag = initialPopTag ? String(initialPopTag) : null;

    activePopTagRef.current = requestedTag;
    setActivePopTag(requestedTag);

    const initializeFeed = async () => {
      try {
        const response = await getThumbsRef.current();

        if (cancelled || generation !== generationRef.current) {
          return;
        }

        const records = Array.isArray(response)
          ? response
          : Array.isArray(response?.pops)
            ? response.pops
            : Array.isArray(response?.popThumbs)
              ? response.popThumbs
              : [];

        void loadViewCounts(
          records.map((pop) => pop.popId),
          generation,
        );

        if (requestedTag) {
          const selectedResponse = await getPopRef.current(requestedTag);

          if (cancelled || generation !== generationRef.current) {
            return;
          }

          const selected = normalizePop(selectedResponse);

          if (selected?.videoUrl) {
            const tag = String(selected.popTag);

            setInitialPop(selected);

            playablePopsRef.current = {
              ...playablePopsRef.current,
              [tag]: selected,
            };

            setPlayablePops((previous) => ({
              ...previous,
              [tag]: selected,
            }));

            activePopTagRef.current = tag;
            setActivePopTag(tag);

            if (selected.popId) {
              void loadViewCounts([selected.popId], generation);
            }

            return;
          }
        }

        if (records.length) {
          const tag = String(records[0].popTag);

          activePopTagRef.current = tag;
          setActivePopTag(tag);
        }
      } catch (loadError) {
        console.error("Failed loading group Pops:", loadError);
      }
    };

    void initializeFeed();

    return () => {
      cancelled = true;
    };
  }, [groupTag, initialPopTag, loadViewCounts]);

  useEffect(() => {
    return () => {
      resetRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!feedPops.length) return;

    const exists = feedPops.some(
      (pop) => String(pop.popTag) === String(activePopTagRef.current),
    );

    if (!exists) {
      const firstTag = String(feedPops[0].popTag);

      activePopTagRef.current = firstTag;
      setActivePopTag(firstTag);
    }
  }, [feedPops]);

  useEffect(() => {
    if (!feedPops.length) return;

    void loadViewCounts(feedPops.map((pop) => pop.popId));
  }, [feedPops, loadViewCounts]);

  useEffect(() => {
    const ids = [
      ...new Set(
        [...feedPops, ...Object.values(playablePops)]
          .map((pop) => Number(pop?.popId))
          .filter((id) => Number.isSafeInteger(id) && id > 0),
      ),
    ];

    ids.forEach((id) => {
      const key = String(id);

      if (
        loadedSavedIdsRef.current.has(key) ||
        pendingSavedIdsRef.current.has(key)
      ) {
        return;
      }

      if (hasSavedStatusRef.current(id)) {
        loadedSavedIdsRef.current.add(key);
        return;
      }

      pendingSavedIdsRef.current.add(key);

      Promise.resolve()
        .then(() => getSavedStatusRef.current(id))
        .then(() => {
          if (hasSavedStatusRef.current(id)) {
            loadedSavedIdsRef.current.add(key);
          }
        })
        .catch((savedError) => {
          console.error(
            `Unable to load saved status for Pop ${id}:`,
            savedError,
          );
        })
        .finally(() => {
          pendingSavedIdsRef.current.delete(key);
        });
    });
  }, [feedPops, playablePops]);

  useEffect(() => {
    if (!activePopTag || !feedPops.length) return;

    const index = feedPops.findIndex(
      (pop) => String(pop.popTag) === String(activePopTag),
    );

    void loadPlayablePop(activePopTag);

    const nextPop = index >= 0 ? feedPops[index + 1] : null;

    if (nextPop?.popTag) {
      void loadPlayablePop(nextPop.popTag);
    }
  }, [activePopTag, feedPops, loadPlayablePop]);

  useLayoutEffect(() => {
    if (initialScrollRef.current || !initialPopTag || !feedPops.length) {
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const target = Array.from(
      container.querySelectorAll(".user-group-pops-feed__item[data-pop-tag]"),
    ).find((item) => String(item.dataset.popTag) === String(initialPopTag));

    if (!target) return;

    container.scrollTop = target.offsetTop - container.offsetTop;

    initialScrollRef.current = true;
  }, [feedPops, initialPopTag]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !feedPops.length) return;

    const items = Array.from(
      container.querySelectorAll(".user-group-pops-feed__item[data-pop-tag]"),
    );

    const ratios = new Map();

    items.forEach((item) => {
      ratios.set(String(item.dataset.popTag), 0);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const tag = entry.target.dataset.popTag;
          if (!tag) return;

          ratios.set(
            String(tag),
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        });

        let selectedTag = null;
        let highestRatio = 0;

        ratios.forEach((ratio, tag) => {
          if (ratio > highestRatio) {
            highestRatio = ratio;
            selectedTag = tag;
          }
        });

        if (!selectedTag || highestRatio < 0.45) {
          return;
        }

        if (selectedTag === String(activePopTagRef.current)) {
          return;
        }

        activePopTagRef.current = selectedTag;
        setActivePopTag(selectedTag);
      },
      {
        root: container,
        threshold: [0, 0.25, 0.45, 0.6, 0.75, 1],
      },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [feedPops]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const videos = Array.from(container.querySelectorAll("video"));

    videos.forEach((video) => {
      const item = video.closest(".user-group-pops-feed__item");

      const tag = item?.dataset.popTag;
      if (!tag) return;

      const isActive = String(tag) === String(activePopTag);

      video.muted = muted;
      video.playsInline = true;

      if (!isActive || document.hidden) {
        video.pause();
        return;
      }

      const startPlayback = async () => {
        try {
          if (video.readyState === 0) {
            video.load();
          }

          await video.play();
        } catch (playError) {
          if (playError?.name === "AbortError") return;

          if (!video.muted) {
            video.muted = true;

            try {
              await video.play();
            } catch (retryError) {
              if (retryError?.name !== "AbortError") {
                console.warn("Group Pop playback failed:", retryError);
              }
            }
          } else {
            console.warn("Group Pop playback failed:", playError);
          }
        }
      };

      void startPlayback();
    });
  }, [activePopTag, muted, playablePops]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleVisibilityChange = () => {
      const videos = container.querySelectorAll("video");

      videos.forEach((video) => {
        const item = video.closest(".user-group-pops-feed__item");

        const isActive =
          String(item?.dataset.popTag) === String(activePopTagRef.current);

        if (document.hidden || !isActive) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const trigger = loadMoreTriggerRef.current;

    if (!container || !trigger || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          loadingMoreRef.current ||
          requestingMoreRef.current
        ) {
          return;
        }

        requestingMoreRef.current = true;

        Promise.resolve()
          .then(() => loadMoreFnRef.current?.())
          .catch((loadError) => {
            console.error("Failed loading more group Pops:", loadError);
          })
          .finally(() => {
            requestingMoreRef.current = false;
          });
      },
      {
        root: container,
        rootMargin: "800px 0px",
        threshold: 0,
      },
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [hasMore, feedPops.length]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleSavePop = async (popId) => {
    const id = Number(popId);

    if (!Number.isSafeInteger(id) || id <= 0 || isPopSaving(id)) {
      return false;
    }

    return toggleSavePop(id);
  };

  const refreshRecordedView = async (popId, generation) => {
    const key = String(popId);

    try {
      const results = await getViewCountsRef.current([popId]);

      if (!mountedRef.current || generation !== generationRef.current) {
        return;
      }

      applyViewCounts(results);
    } catch (viewError) {
      console.error("Unable to refresh group Pop views:", viewError);
    } finally {
      if (generation === generationRef.current) {
        pendingViewIdsRef.current.delete(key);
      }
    }
  };

  const handleVideoTimeUpdate = (event, pop, active) => {
    if (!active || !pop?.popId || document.hidden) {
      return;
    }

    const video = event.target;

    if (!(video instanceof HTMLVideoElement)) {
      return;
    }

    if (video.paused || video.seeking || video.readyState < 2) {
      return;
    }

    const popId = Number(pop.popId);

    if (!Number.isSafeInteger(popId) || popId <= 0) {
      return;
    }

    const key = String(popId);
    const currentTime = Number(video.currentTime);

    if (!Number.isFinite(currentTime)) return;

    const previous = watchProgressRef.current.get(key) || {
      lastTime: null,
      watchSeconds: 0,
    };

    let watchSeconds = previous.watchSeconds;

    if (previous.lastTime != null) {
      const delta = currentTime - previous.lastTime;

      if (delta > 0 && delta <= 1.5) {
        watchSeconds += delta;
      }
    }

    watchProgressRef.current.set(key, {
      lastTime: currentTime,
      watchSeconds,
    });

    if (viewAttemptsRef.current.has(key) || watchSeconds < MIN_WATCH_SECONDS) {
      return;
    }

    const duration = Number(video.duration);

    const completionPercent =
      Number.isFinite(duration) && duration > 0
        ? Math.min(100, (currentTime / duration) * 100)
        : 0;

    viewAttemptsRef.current.add(key);

    const generation = generationRef.current;

    Promise.resolve()
      .then(() =>
        recordPopView(popId, {
          watchSeconds: Number(watchSeconds.toFixed(2)),
          completionPercent: Number(completionPercent.toFixed(2)),
          referrerType: "group",
        }),
      )
      .then(async (result) => {
        if (!result) {
          viewAttemptsRef.current.delete(key);
          console.error("Unable to record group Pop view:", popId);
          return;
        }

        if (!mountedRef.current || generation !== generationRef.current) {
          return;
        }

        loadedViewIdsRef.current.delete(key);

        await refreshRecordedView(popId, generation);
      })
      .catch((viewError) => {
        if (generation === generationRef.current) {
          viewAttemptsRef.current.delete(key);
        }

        console.error("Group Pop view tracking failed:", viewError);
      });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const items = container.querySelectorAll(
      ".user-group-pops-feed__item[data-pop-id]",
    );

    items.forEach((item) => {
      if (String(item.dataset.popTag) === String(activePopTag)) {
        return;
      }

      const key = String(item.dataset.popId);
      const previous = watchProgressRef.current.get(key);

      if (previous) {
        watchProgressRef.current.set(key, {
          ...previous,
          lastTime: null,
        });
      }
    });
  }, [activePopTag]);

  const handleRetryPop = (popTag) => {
    const key = String(popTag);

    const updated = {
      ...playablePopsRef.current,
    };

    delete updated[key];

    playablePopsRef.current = updated;
    setPlayablePops(updated);

    setVideoErrors((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });

    void loadPlayablePop(key);
  };

  return (
    <div
      className="user-group-pops-feed__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Group Pops player"
    >
      <div className="user-group-pops-feed">
        {showCloseButton && onClose && (
          <button
            type="button"
            className="user-group-pops-feed__close"
            onClick={onClose}
            aria-label="Close Pops feed"
          >
            <X size={22} aria-hidden="true" />
          </button>
        )}

        <div ref={containerRef} className="user-group-pops-feed__container">
          {feedPops.map((pop) => {
            const tag = String(pop.popTag);
            const active = tag === String(activePopTag);
            const playable = playablePops[tag];
            const popId = playable?.popId ?? pop.popId;
            const key = String(popId);

            const saved = isPopSaved(popId);
            const saving = isPopSaving(popId);

            const fetched = viewCounts[key];

            const fallbackRealViews = normalizeCount(
              playable?.realViews ??
                pop.realViews ??
                playable?.views ??
                pop.views,
            );

            const realViews = fetched
              ? fetched.realViews
              : getPopViewCount(popId, fallbackRealViews);

            const demoViews = fetched
              ? fetched.demoViews
              : normalizeCount(playable?.demoViews ?? pop.demoViews);

            const totalViews = fetched
              ? fetched.totalViews
              : normalizeCount(
                  playable?.totalViews ?? pop.totalViews,
                  realViews + demoViews,
                );

            const displayPop = {
              ...pop,
              ...(playable || {}),
              popId,
              views: totalViews,
              realViews,
              demoViews,
              totalViews,
              isSaved: saved,
              userViewCount: normalizeCount(playable?.userViewCount),
              totalWatchSeconds: normalizeCount(playable?.totalWatchSeconds),
              averageCompletion: normalizeCount(playable?.averageCompletion),
              videoUrl:
                playable?.videoUrl || pop.videoUrl || pop.playbackUrl || "",
              thumbnailUrl: playable?.thumbnailUrl || pop.thumbnailUrl || null,
            };

            return (
              <section
                key={tag}
                data-pop-tag={tag}
                data-pop-id={popId || ""}
                className={`user-group-pops-feed__item ${
                  active ? "user-group-pops-feed__item--active" : ""
                }`}
                onTimeUpdateCapture={(event) =>
                  handleVideoTimeUpdate(event, displayPop, active)
                }
                onSeekedCapture={() => {
                  const progress = watchProgressRef.current.get(key);

                  if (progress) {
                    watchProgressRef.current.set(key, {
                      ...progress,
                      lastTime: null,
                    });
                  }
                }}
              >
                {videoErrors[tag] ? (
                  <div className="user-group-pops-feed__state">
                    <p className="user-group-pops-feed__state-message">
                      {videoErrors[tag]}
                    </p>

                    <button type="button" onClick={() => handleRetryPop(tag)}>
                      Retry video
                    </button>
                  </div>
                ) : displayPop.videoUrl ? (
                  <PopThumbnailCard
                    pop={displayPop}
                    mode="player"
                    active={active}
                    muted={muted}
                    onMuteChange={setMuted}
                    onSave={() => handleSavePop(popId)}
                    isSaved={saved}
                    saving={saving}
                  />
                ) : (
                  <div className="user-group-pops-feed__state">
                    <div className="user-group-pops-feed__loader" />
                    <span>Loading video...</span>
                  </div>
                )}
              </section>
            );
          })}

          {loadingMore && (
            <div className="user-group-pops-feed__load-more">
              <div className="user-group-pops-feed__loader" />
              <span>Loading more Pops...</span>
            </div>
          )}

          {hasMore && (
            <div
              ref={loadMoreTriggerRef}
              className="user-group-pops-feed__load-trigger"
              aria-hidden="true"
            />
          )}

          {!hasMore && feedPops.length > 0 && (
            <div className="user-group-pops-feed__end">
              You're all caught up.
            </div>
          )}

          {loading && feedPops.length === 0 && (
            <div className="user-group-pops-feed__state">
              <div className="user-group-pops-feed__loader" />
              <span>Loading Pops...</span>
            </div>
          )}

          {!loading && !error && feedPops.length === 0 && (
            <div className="user-group-pops-feed__state">
              <span>No Pops available in this group.</span>
            </div>
          )}

          {error && feedPops.length === 0 && (
            <div className="user-group-pops-feed__state">
              <p className="user-group-pops-feed__state-message">
                {typeof error === "string"
                  ? error
                  : error?.message || "Unable to load Pops."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupMemberPopsFeed;
